import { CircularProgress, LinearProgress, Link } from "@mui/material";
import DetailsPanel from "components/Elements/DetailsPanel";
import SearchField from "components/Elements/SearchField/SearchField";
import {
	isoDateWithoutTimeZone,
	debounce,
	getLocalStorageData,
	defaultPageSize,
} from "helpers/utils";
import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { showError } from "../../../redux/common/actions";
import AddNewFeedbackDetail from "./AddFeedback";
import Header from "./Header";
import { makeStyles } from "tss-react/mui";
import FeedbackListTable from "./FeedbackListTable";
import ColourConstants from "helpers/colourConstants";
import CustomDateRange from "./CustomDateRange";
import DeleteDialog from "components/Elements/DeleteDialog";
import {
	getCountOfFeedbackList,
	getFeedbackList,
	getFeedbackUsers,
	postNewFeedback,
} from "services/feedback/feedbackList";
import {
	FeedbackTableColumns,
	FeedbackTableHeader,
} from "constants/FeedbackDetails";
import TabTitle from "components/Elements/TabTitle";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import { READONLY_ACCESS } from "constants/AccessTypes/AccessTypes";
import { appPath, feedbackPath } from "helpers/routePaths";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";
import {
	makeGetNextPageParam,
	removeRowFromInfiniteCache,
	flattenPages,
} from "helpers/infiniteQuery";

const useStyles = makeStyles()((theme) => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
		right: 0,
	},
}));

export const isRelevantFeedback = (feedback, me) => {
	return (
		feedback.assignUserID === me.userId ||
		(feedback.assignUserID === null &&
			feedback.assignPositionID === me.positionId) ||
		feedback.createdUserID === me.userId
	);
};

const formattedData = (data, navigate, me) => {
	return data.map((x) => ({
		...x,
		number: (
			<Link
				onClick={() => navigate(`${x.id}`)}
				href={`${appPath}${feedbackPath}/${x.id}`}
				style={{
					color: ColourConstants.activeLink,
					cursor: "pointer",
					textDecoration: "none",
				}}
			>
				{x.number}
			</Link>
		),
		createdDateTime: isoDateWithoutTimeZone(x.createdDateTime + "Z"),
		benefit: (
			<HtmlTooltip title={x.benefit}>
				<p className="max-two-line">
					<Link
						onClick={() => navigate(`${x.id}`)}
						href={`${appPath}${feedbackPath}/${x.id}`}
						style={{
							color: ColourConstants.commonText,
							cursor: "pointer",
							textDecoration: "none",
						}}
					>
						{x.benefit}
					</Link>
				</p>
			</HtmlTooltip>
		),
		changeRequired: (
			<HtmlTooltip title={x.changeRequired}>
				<p className="max-two-line">
					<Link
						onClick={() => navigate(`${x.id}`)}
						href={`${appPath}${feedbackPath}/${x.id}`}
						style={{
							color: ColourConstants.commonText,
							cursor: "pointer",
							textDecoration: "none",
						}}
					>
						{x.changeRequired}
					</Link>
				</p>
			</HtmlTooltip>
		),
		feedbackStatusName: (
			<span
				style={{
					color: x?.feedbackStatusType === "C" ? ColourConstants.green : "",
				}}
			>
				{x?.feedbackStatusName}
			</span>
		),
	}));
};

const defaultCustomDate = { from: "", to: "" };

const FEEDBACK_LIST_KEY = ["feedback", "list"];
const FEEDBACK_COUNT_KEY = ["feedback", "count"];

// Filter configuration for the new Filter framework
const filterConfig = {
	filters: {
		user: { fetchFn: getFeedbackUsers },
		feedbackStatus: true,
		timeframe: {
			allowCustomRange: true,
			allowedRanges: ['today', 'yesterday', 'this-week', 'last-week', 'last-month', 'last-year', 'last-7-days', 'last-14-days', 'last-30-days', 'last-90-days', 'custom']
		},
		department: { multiSelect: true },
		tableSort: true,
	}
};

function FeedbackListsContent() {
	const { t } = useTranslation();
	const { filterValues, updateFilter, allFiltersInitialized } = useFilters();
	const queryClient = useQueryClient();

	const dispatch = useDispatch();
	const { classes } = useStyles();

	const {
		customCaptions,
		siteAppID,
		siteID,
		id,
		application,
		position: { id: positionId, feedbackAccess },
	} = getLocalStorageData("me");

	const tableSortParams = filterValues.tableSort?.params || { sortField: "", sortOrder: "asc" };
	const currentTableSort = [tableSortParams.sortField, tableSortParams.sortOrder];
	const [openAddFeedback, setOpenAddFeedback] = useState(false);
	const [loading, setLoading] = useState(true);
	const [isSearching, setSearching] = useState(false);
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

	const [customDate, setCustomDate] = useState(defaultCustomDate);
	const [openCustomDatePopup, setOpenCustomDatePopup] = useState(false);
	const [isCustomDateRangeError, setCustomDateRangeError] = useState(false);
	const [dataForFetchingFeedback, setDataForFetchingFeedback] = useState({
		pageNumber: 1,
		pageSize: defaultPageSize(),
		search: "",
		sortField: "",
		sort: "",
	});

	const searchRef = useRef("");
	const [pageSize] = useState(() => defaultPageSize());

	const queryInputs = useMemo(() => {
		const statusValue = filterValues['feedbackStatus']?.value;
		const timeframeValue = filterValues['timeframe']?.value;
		const departmentParams = filterValues['department']?.params || {};
		const feedbackStatusID = statusValue?.id || '';
		// Status / statusType split as in the original code.
		return {
			siteAppId: siteAppID,
			feedbackStatusID: ["C", "O"].includes(feedbackStatusID) ? "" : feedbackStatusID,
			statusType: ["C", "O"].includes(feedbackStatusID) ? feedbackStatusID : (statusValue?.statusType || ''),
			siteDepartmentID: departmentParams.siteDepartmentID || '',
			siteDepartmentIds: departmentParams.siteDepartmentIds || [],
			fromDate: timeframeValue?.fromDate || '',
			toDate: timeframeValue?.toDate || '',
			myFeedback: filterValues['user']?.params?.myFeedback || false,
			userId: filterValues['user']?.params?.userId || '',
			sortField: tableSortParams.sortField || '',
			sortOrder: tableSortParams.sortOrder || 'asc',
		};
	}, [
		siteAppID,
		filterValues['feedbackStatus']?.value?.id,
		filterValues['feedbackStatus']?.value?.statusType,
		filterValues['department']?.params,
		filterValues['timeframe']?.value?.fromDate,
		filterValues['timeframe']?.value?.toDate,
		filterValues['user']?.params?.myFeedback,
		filterValues['user']?.params?.userId,
		tableSortParams.sortField,
		tableSortParams.sortOrder,
	]);

	const feedbackQueryKey = useMemo(
		() => [...FEEDBACK_LIST_KEY, { ...queryInputs, pageSize }],
		[queryInputs, pageSize]
	);

	const feedbackQuery = useInfiniteQuery({
		queryKey: feedbackQueryKey,
		enabled: allFiltersInitialized && Boolean(siteAppID),
		initialPageParam: 1,
		queryFn: async ({ pageParam }) => {
			const response = await getFeedbackList({
				...queryInputs,
				search: searchRef.current,
				pageNumber: pageParam,
				pageSize,
			});
			if (!response?.status) {
				throw new Error(
					response?.data?.detail ||
						t("message.failedToFetchEntityList", {
							entity: customCaptions?.feedback,
						})
				);
			}
			return { rows: Array.isArray(response.data) ? response.data : [], pageParam };
		},
		getNextPageParam: makeGetNextPageParam(pageSize),
	});

	useEffect(() => {
		if (feedbackQuery.isError) {
			dispatch(
				showError(
					feedbackQuery.error?.message ||
						t("message.failedToFetchEntityList", {
							entity: customCaptions?.feedback,
						})
				)
			);
		}
	}, [feedbackQuery.isError, feedbackQuery.error, dispatch, customCaptions?.feedback]);

	const countQueryKey = useMemo(
		() => [...FEEDBACK_COUNT_KEY, queryInputs],
		[queryInputs]
	);

	const countQuery = useQuery({
		queryKey: countQueryKey,
		enabled: allFiltersInitialized && Boolean(siteAppID),
		queryFn: async () => {
			const response = await getCountOfFeedbackList({
				...queryInputs,
				search: searchRef.current,
			});
			return response?.status ? (response.data?.totalCount ?? 0) : 0;
		},
	});

	const allData = useMemo(() => flattenPages(feedbackQuery.data), [feedbackQuery.data]);
	const countOFFeedback = countQuery.data ?? 0;

	const refreshFeedback = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: FEEDBACK_LIST_KEY });
		return queryClient.invalidateQueries({ queryKey: FEEDBACK_COUNT_KEY });
	}, [queryClient]);

	// Custom date popup wiring (unchanged in spirit — filter framework owns the date).
	const handleCustomDateChange = (type, e) => {
		setCustomDate({ ...customDate, [type]: e.target.value });
	};

	const handleCloseCustomDate = () => {
		setOpenCustomDatePopup(false);
		setCustomDateRangeError(defaultCustomDate);
	};

	const handleCustomDateSubmit = async (e) => {
		e.preventDefault();
		const isFromDateEmpty = customDate.from === "";
		const isToDateEmpty = customDate.to === "";
		if (isFromDateEmpty || isToDateEmpty) {
			setCustomDateRangeError({ from: isFromDateEmpty, to: isToDateEmpty });
			return;
		}
		handleCloseCustomDate();
	};

	useEffect(() => {
		setLoading(false);
		document.body.style.overflowX = "hidden";
		document.body.style.maxWidth = "100%";
	}, [siteID]);

	// Search just changes the underlying ref; we then trigger a refetch by
	// invalidating the query.
	const handleSearch = useCallback(
		debounce(
			async (value) => {
				setSearching(true);
				searchRef.current = value;
				await refreshFeedback();
				if (!value || value === "")
					setDataForFetchingFeedback({
						pageNumber: 1,
						pageSize: defaultPageSize(),
						search: "",
						sortField: "",
						sort: "",
					});
				setSearching(false);
			},
			500
		),
		[refreshFeedback]
	);

	const addNewFeedback = async (payload) => {
		return await postNewFeedback(payload);
	};

	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);
		setOpenDeleteDialog(true);
	};

	const handleRemoveData = (id) => {
		removeRowFromInfiniteCache(queryClient, feedbackQueryKey, (row) => row.id === id);
		queryClient.invalidateQueries({ queryKey: FEEDBACK_COUNT_KEY });
	};

	if (loading) return <CircularProgress />;

	const tableIsRefreshing =
		(feedbackQuery.isFetching && !feedbackQuery.isFetchingNextPage) ||
		countQuery.isFetching;

	return (
		<>
			<TabTitle
				title={`${customCaptions.feedbackPlural} | ${application.name}`}
			/>
			{(isSearching || tableIsRefreshing) && <LinearProgress className={classes.loading} />}

			<DeleteDialog
				entityName={customCaptions?.feedback}
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteID={deleteID}
				deleteEndpoint={"/api/feedback"}
				handleRemoveData={handleRemoveData}
			/>
			<AddNewFeedbackDetail
				open={openAddFeedback}
				closeHandler={() => setOpenAddFeedback(false)}
				siteAppId={siteAppID}
				title={t("action.addEntity", { entity: customCaptions?.feedback })}
				createProcessHandler={addNewFeedback}
				customCaptions={customCaptions}
				showAssetReferences={application?.showAssetReferences}
				feedbackRequireDepartment={application?.feedbackRequireDepartment}
				setSearchQuery={() => {}}
				fetchData={refreshFeedback}
				setDataForFetchingFeedback={setDataForFetchingFeedback}
				siteID={siteID}
			/>
			<CustomDateRange
				open={openCustomDatePopup}
				handleChange={handleCustomDateChange}
				customDate={customDate}
				closeHandler={handleCloseCustomDate}
				onSubmit={handleCustomDateSubmit}
				isError={isCustomDateRangeError}
				isLoading={isSearching}
			/>
			<div className="container">
				<Header
					setOpenAddFeedback={setOpenAddFeedback}
					dataLength={countOFFeedback}
					feedbackCC={customCaptions?.feedbackPlural}
				/>
				<div
					className="detailsContainer"
					style={{ alignItems: "center", marginTop: "-15px" }}
				>
					<DetailsPanel
						showHeader={false}
						description={t("message.viewAllEntities", {
							entity: customCaptions?.feedbackPlural,
						})}
					/>
					<SearchField
						searchQuery={dataForFetchingFeedback?.search}
						setSearchQuery={(e) => {
							e.persist();
							setDataForFetchingFeedback((prev) => ({
								...prev,
								search: e.target.value,
							}));
							handleSearch(e.target.value);
						}}
					/>
				</div>


				{/* Filter Framework */}
				<div style={{ marginTop: 20 }}>
					<FilterContainer />
				</div>

				<div style={{ height: 20 }}></div>
				<div className="dynamic-width-table">
					<FeedbackListTable
						data={allData}
						headers={FeedbackTableHeader(customCaptions)}
						columns={FeedbackTableColumns()}
						handleSort={(sortField, sort) => {
							const sortId = sortField ? `${sortField}_${sort}` : '';
							const sortName = sortField ? `${sortField} (${sort === 'asc' ? 'Ascending' : 'Descending'})` : 'Default Sort';
							updateFilter('tableSort', { id: sortId, name: sortName }, {
								sortField,
								sortOrder: sort
							});
						}}
						formattedData={formattedData}
						handleDeleteDialogOpen={handleDeleteDialogOpen}
						currentTableSort={currentTableSort}
						setCurrentTableSort={(sortArray) => {
							const sortField = sortArray[0];
							const sortOrder = sortArray[1];
							const sortId = sortField ? `${sortField}_${sortOrder}` : '';
							const sortName = sortField ? `${sortField} (${sortOrder === 'asc' ? 'Ascending' : 'Descending'})` : 'Default Sort';
							updateFilter('tableSort',
								{ id: sortId, name: sortName },
								{ sortField, sortOrder }
							);
						}}
						isReadOnly={feedbackAccess === READONLY_ACCESS}
						hasNextPage={feedbackQuery.hasNextPage}
						isFetchingNextPage={feedbackQuery.isFetchingNextPage}
						fetchNextPage={feedbackQuery.fetchNextPage}
						isFetchNextPageError={feedbackQuery.isFetchNextPageError}
					/>
				</div>
			</div>
		</>
	);
}

// Wrapper component that provides FilterContext
function FeedbackLists() {
	return (
		<FilterProvider
			config={filterConfig}
			storageKey="feedback"
		>
			<FeedbackListsContent />
		</FilterProvider>
	);
}

export default FeedbackLists;
